import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { SupportIssueType } from '../../layout/SupportIssueModal/components/SupportIssueModalContent';
import type { FileWithId } from '../store/newMediaStore';
import type { ApiRequestObject } from './types/api-request';
import type { ContentObject, Media, User } from './types/api-response';

import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import moment from 'moment';

import { getToken } from '../../hooks/useToken';

import image1 from '../../images/graphics/type-definition-card-backgrounds-placeholders/card_bg-1-placeholder.jpg';
import image2 from '../../images/graphics/type-definition-card-backgrounds-placeholders/card_bg-2-placeholder.jpg';
import image3 from '../../images/graphics/type-definition-card-backgrounds-placeholders/card_bg-3-placeholder.jpg';
import image4 from '../../images/graphics/type-definition-card-backgrounds-placeholders/card_bg-4-placeholder.jpg';

import i18n from '../../i18n';
import { getEnv } from '../env';
import {
  formatErrorToString,
  generateRedirectUrl,
  getDefaultImageProperties,
  isImagePreviewSupported,
  strToChecksum,
} from '../helpers';
import { resetPasswordRequest } from './auth-requests';
import {
  exportAllSpaces,
  exportAllUsers,
  getContentObjects,
  listContentTypes,
  postContentObject,
  postMedia,
  postOrder,
  postSupportIssue,
  putUserConfig,
  synchronizationImport,
  updateMedia,
} from './index';
import { parseResponseErrors, ResponseError } from './response-errors';

const ctdFeaturedImages = [image1, image2, image3, image4];

const apiUrl = getEnv('VITE_FLOTIQ_API_URL') || '';

export const defaultToDo = {
  publicDoc: {
    title: 'Visit api documentation',
    status: 'todo',
  },
  contentType: {
    title: 'Add content type definition',
    status: 'todo',
  },
  contentObject: {
    title: 'Add content object',
    status: 'todo',
  },
  gatsbyStarters: {
    title: 'Try our starter projects',
    status: 'todo',
  },
};

/**
 * Generate url to media with specified parameters
 */
export function getMediaUrl(
  mediaData: any,
  desiredWidth: number = 0,
  desiredHeight: number = 0,
  variantName: string = '',
) {
  const url = `${apiUrl.replace(/\/{1,10}$/, '')}/image`;
  const size =
    mediaData?.type !== 'image' ? '0x0' : `${desiredWidth}x${desiredHeight}`;

  let path = `${url}/${size}/${mediaData.id}`;

  if (getEnv('VITE_ADD_FILENAME_TO_MEDIA_URL') === 'true' && !variantName) {
    path += `/${encodeURIComponent(mediaData.fileName).replace(
      /\.(?=.*\.)/g,
      '-',
    )}`;
  } else {
    path += `.${mediaData.extension}`;
  }

  if (variantName) {
    path += `/variant/${variantName}`;
  }

  return path;
}

/**
 * Generate url to media with specified transform
 */
export function getMediaTransformUrl(
  mediaData: any,
  left: number,
  top: number,
  transformWidth: number,
  transformHeight: number,
  desiredWidth: number = 0,
  desiredHeight: number = 0,
) {
  if (mediaData?.type !== 'image') return;

  const url = `${apiUrl.replace(/\/{1,10}$/, '')}/image`;
  const size = `${desiredWidth}x${desiredHeight}`;

  const path = `${url}/${size}/${mediaData.id}.${mediaData.extension}`;

  return `${path}/transform/trim/${top},${left},${transformWidth},${transformHeight}`;
}

/**
 * Get featured image for content type
 */
export const getCtdFeaturedImage = (
  contentTypeName?: string,
  featuredImage?: Media,
  width: number = 600,
) => {
  if (featuredImage && featuredImage.id) {
    return getMediaUrl(featuredImage, width);
  }
  return ctdFeaturedImages[Math.abs(strToChecksum(contentTypeName || '') % 4)];
};

/**
 * Update user tasks list
 */
export async function markTaskAsDone(
  taskName: string,
  user: any,
  jwt?: string,
  setUser?: any,
  contentTypeSlug: string = '',
) {
  try {
    const toDoList = JSON.parse(JSON.stringify(defaultToDo));
    if (!user?.data) return;

    const newConfig =
      user.data.config && !Array.isArray(user.data.config)
        ? { ...user.data.config }
        : {};

    if (!newConfig.todo) {
      newConfig.todo = toDoList;
    }

    if (!newConfig.todo[taskName]) {
      if (!toDoList[taskName]) {
        throw new Error(`Task doesn't exists ${taskName}`);
      }
      newConfig.todo[taskName] = toDoList[taskName];
    }

    const status = newConfig.todo[taskName].status;
    if (status === 'done' && !contentTypeSlug) return;

    newConfig.todo[taskName].status = 'done';
    if (contentTypeSlug)
      newConfig.todo[taskName].contentTypeSlug = contentTypeSlug;

    const { ok } = await putUserConfig(jwt, undefined, {
      id: user.data.id,
      config: newConfig,
    });

    if (setUser && ok)
      setUser({ ...user, data: { ...user.data, config: newConfig } });
  } catch (e) {
    console.warn(e);
  }
}

/**
 * Update user tasks list
 */
export async function removeDowngradedFlags(
  queryClient: QueryClient,
  user?: User | null,
  jwt?: string,
) {
  try {
    if (!user) return;

    const newConfig: User['config'] | null =
      user.config && !Array.isArray(user.config) ? { ...user.config } : null;

    if (!newConfig?.downgraded_notice) return;

    delete newConfig.downgraded_notice;

    await putUserConfig(jwt, undefined, {
      id: user.id,
      config: newConfig,
    });

    queryClient.invalidateQueries({ queryKey: ['users'] });
  } catch (e) {
    console.warn(e);
  }
}

/**
 * Uploads file and handles upload errors. Returns array containing result on first element and errors on the second.
 */
export async function uploadFile(
  jwt: string,
  space: string | undefined,
  file: FileWithId | undefined,
  t: TFunction,
) {
  if (!file) return;
  try {
    const formData = new FormData();
    formData.append('file', file, file.name);
    let type = file.type.includes('image') ? 'image' : 'file';
    const ext = file.name.split('.').pop();
    if (ext === 'img') {
      type = 'file';
    }
    formData.append('type', type);
    const { body, ok } = await postMedia(
      jwt,
      space,
      {},
      {
        skipContentType: true,
        body: formData,
      },
    );
    if (!ok) {
      throw new Error(formatErrorToString(body));
    }
    const showImages = isImagePreviewSupported(body.extension);

    toast(t('Media.OnAddSuccess', { name: body.fileName }), {
      icon: (
        <div className="h-12 w-12 mr-2">
          {body.type === 'image' && showImages ? (
            <img
              src={getMediaUrl(body, 150)}
              alt={t('Media.OnAddSuccess', { name: body.fileName })}
              className="h-full w-full object-center object-cover"
              {...getDefaultImageProperties()}
            />
          ) : (
            <div className="flex items-center justify-center rounded-full h-full w-full bg-blue-300">
              {body.extension}
            </div>
          )}
        </div>
      ),
    });
    return { media: body, state: 'uploaded' };
  } catch (e) {
    const error = (e as Error).message || t('Media.OnErrorMessage');
    toast.error(error);
    return { media: file, state: 'error', error };
  }
}

export async function createSupportIssue(
  jwt: string,
  space: string | undefined,
  payload: SupportIssueType,
  t: TFunction,
) {
  const formData = new FormData();
  formData.append('content', payload.content);
  formData.append('metadata', payload.metadata);

  for (const file of payload.files || []) {
    formData.append('files[]', file, file.name);
  }

  try {
    const { body, status, ok } = await postSupportIssue(
      jwt,
      space,
      {},
      {
        skipContentType: true,
        body: formData,
      },
    );
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    return ok;
  } catch (error) {
    toast.error(
      t(
        error instanceof ResponseError
          ? 'SupportIssueModal.Form.ErrorMessage'
          : 'Form.CommunicationErrorMessage',
      ),
    );
    return false;
  }
}

export async function synchronizationImportZip(
  jwt: string,
  space: string | undefined,
  payload: { files: File[] },
  t: TFunction,
) {
  const formData = new FormData();
  const [file] = payload.files;

  formData.append('spaceId', space || '');
  formData.append('file', file, file.name);

  try {
    const { body, status, ok } = await synchronizationImport(
      jwt,
      space,
      {},
      {
        skipContentType: true,
        body: formData,
      },
    );
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    return { ok, status };
  } catch (error) {
    toast.error(
      t(
        error instanceof ResponseError
          ? 'SpaceSynchronization.Import.ImportFailedMessage'
          : 'Form.CommunicationErrorMessage',
      ),
    );
    return { ok: false, status: undefined };
  }
}

/**
 * Uploads array of files
 */
export async function uploadFiles(
  jwt: string,
  space: string | undefined,
  files: FileWithId[],
  t: TFunction,
) {
  if (!files || !files.length) return undefined;

  const toastId = toast.loading(
    t('Media.UploadingFiles', { count: files.length }),
  );

  const uploadedMedia: any[] = [];
  await Promise.all(
    Array.from(files).map(async (file) => {
      const media = await uploadFile(jwt, space, file, t);
      if (media) uploadedMedia.push(media);
    }),
  );

  toast.remove(toastId);

  return uploadedMedia;
}

/**
 * Filter tags data that contains the query
 */
export async function getTagsFromQuery(
  jwt: string | undefined,
  space: string | undefined,
  query: string | undefined,
  t: TFunction,
  tagsParams: ApiRequestObject,
) {
  const tagQueryParams = {
    ...tagsParams,
    contentTypeName: '_tag',
    filters: JSON.stringify({
      name: { filter: query, type: 'contains' },
    }),
  };
  let newOptions: ContentObject[] = [];
  try {
    const { body, status, ok } = await getContentObjects(
      jwt,
      space,
      tagQueryParams,
    );
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    newOptions = body.data || [];
  } catch (error) {
    toast.error(
      t(
        error instanceof ResponseError
          ? 'TagsManagement.CouldntFetch'
          : 'Form.CommunicationErrorMessage',
      ),
    );
  }
  return newOptions;
}

/**
 * Handle create/update CTO errors
 */
export const handleCTOErrors = (
  error: any,
  values: any,
  t: TFunction,
  createCTO: boolean = false,
): [ContentObject, Record<string, string | string[]>, boolean] => {
  if (!(error instanceof ResponseError)) {
    toast.error(t('Form.CommunicationErrorMessage'));
    return [values, {}, true];
  }

  if (!error.errors || !Object.keys(error.errors).length) {
    toast.error(
      createCTO ? t('ContentForm.CouldntAdd') : t('ContentForm.CouldntSave'),
    );
    return [values, {}, true];
  }

  return [
    values,
    typeof error.errors === 'string' ? { '': error.errors } : error.errors,
    true,
  ];
};

/**
 * Update content type. Returns result that contains new cto values, fetch errors and boolean value if request failed
 */
export async function updateCTO(
  values: any,
  contentTypeName: string | undefined,
  updateContentObject: any,
  t: TFunction,
  disableSuccessToast: boolean = false,
): Promise<[ContentObject, Record<string, string | string[]>, boolean]> {
  try {
    const { status, body, ok } = await updateContentObject({
      ...values,
      contentTypeName,
    });
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    if (!disableSuccessToast) toast.success(t('ContentForm.Saved'));

    TagManager.dataLayer({
      dataLayer: {
        event: 'co_change',
      },
    });

    return [body, {}, false];
  } catch (error) {
    return handleCTOErrors(error, values, t);
  }
}

/**
 * Save new content type. Returns result that contains new ctd values, fetch errors and boolean value if request failed
 */
export async function saveNewCTO(
  jwt: string | undefined,
  space: string | undefined,
  values: any,
  contentTypeName: string | undefined,
  user: any,
  setUser: any,
  t: TFunction,
): Promise<[ContentObject, Record<string, string | string[]>, boolean]> {
  try {
    const { body, status, ok } = await postContentObject(jwt, space, {
      ...values,
      contentTypeName,
    });
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    toast.success(t('ContentForm.Added'));

    TagManager.dataLayer({
      dataLayer: {
        event: 'co_change',
      },
    });

    await markTaskAsDone('contentObject', user, jwt, setUser);
    return [body, {}, false];
  } catch (error) {
    return handleCTOErrors(error, values, t, true);
  }
}

/**
 * Send change password request
 */
export async function changePasswordRequest(email: string, t: TFunction) {
  try {
    const { body, status, ok } = await resetPasswordRequest(
      email,
      generateRedirectUrl(`/${i18n.language}/change-password`),
    );
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    toast.success(t('Form.FormChangePasswordRequestSuccess'), {
      duration: 10000,
    });
    return null;
  } catch (error) {
    if (!(error instanceof ResponseError)) {
      toast.error(t('Form.CommunicationErrorMessage'));
      return {
        general: t('Form.CommunicationErrorMessage'),
        ...(error as any).errors,
      };
    }
    const errorMessage = error.errors?.form
      ? t('Form.FormErrorEmailSent')
      : error.message;
    const finalError = errorMessage ? errorMessage : t('Form.ValidationError');
    toast.error(finalError);
    return {
      general: finalError,
      ...error.errors,
    };
  }
}

/**
 * Get ctds data from query
 */
export async function getCtdsFromQuery(
  jwt: string | undefined,
  space: string | undefined,
  params: ApiRequestObject | undefined,
  t: TFunction,
) {
  try {
    const { body, status, ok } = await listContentTypes(jwt, space, params);
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    return body.data || [];
  } catch (error) {
    toast.error(
      t(
        error instanceof ResponseError
          ? 'ContentForm.CouldntFetch'
          : 'Form.CommunicationErrorMessage',
      ),
    );
    return [];
  }
}

/**
 * Patch media. Returns list of values, errors and boolean value if contains errors
 */
export async function patchMedia(
  jwt: string | undefined,
  space: string | undefined,
  id: string,
  values: any,
  t: TFunction,
  successText: string = '',
  errorText: string = '',
) {
  try {
    const { status, body, ok } = await updateMedia(jwt, space, {
      mediaId: id,
      ...values,
    });
    if (!ok) {
      throw parseResponseErrors(body, status);
    }
    toast.success(successText || t('MediaEdit.Updated'));

    return [[body, {}], false];
  } catch (error) {
    if (!(error instanceof ResponseError)) {
      toast.error(t('Form.CommunicationErrorMessage'));
      return [[values, {}], true];
    }

    toast.error(error.message || errorText || t('MediaEdit.CouldntUpdate'));

    return [[values, error.errors], true];
  }
}

/**
 * Handle manage subscription button
 */
export async function manageSubscription(
  token: string | undefined,
  userId: string | undefined,
  t: TFunction,
): Promise<void> {
  try {
    const { body } = await postOrder(token, undefined, {
      userId,
      uri: `${getEnv('PUBLIC_URL') || window.origin}/organization`,
    });
    if (body.url) {
      window.open(body.url, '_blank', 'noopener');
    }
  } catch {
    toast.error(t('Form.CommunicationErrorMessage'));
  }
}

const downloadCsvFile = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Generate request to export csv for users or spaces
 */
const exportToCsv = async (
  type: 'users' | 'spaces',
  params?: ApiRequestObject,
  fileNamePrefix?: string,
) => {
  try {
    const token = getToken();
    const response =
      type === 'users'
        ? await exportAllUsers(token, undefined, params)
        : await exportAllSpaces(token, undefined, params);

    const textContent = await response.text();
    if (!response.ok) {
      throw parseResponseErrors(textContent, response.status);
    }

    downloadCsvFile(
      textContent,
      `${fileNamePrefix || type}-${moment().format('YYYY-MM-DD-HH-mm-ss')}.csv`,
    );
    return true;
  } catch (error) {
    console.error(`Error exporting ${type} csv`, error);
    toast.error(i18n.t('ErrorBoundary.SomethingWentWrong'));
    return false;
  }
};

export const exportUsersCsv = async (
  params?: ApiRequestObject,
  fileNamePrefix?: string,
) => {
  return exportToCsv('users', params, fileNamePrefix);
};

export const exportSpacesCsv = async (
  params?: ApiRequestObject,
  fileNamePrefix?: string,
) => {
  return exportToCsv('spaces', params, fileNamePrefix);
};
